/**
 * Typed API client — Task 31.4
 * Axios wrapper with JWT Bearer injection and refresh-token rotation on 401.
 */
import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import type { AuthTokens, ApiError } from '@/types'

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1'

// ─── In-memory token store ────────────────────────────────────────────────────
// Access token lives in memory only; refresh token is an httpOnly cookie set by
// the server, so we never touch it directly from JS.

let accessToken: string | null = null
let isRefreshing = false
let refreshQueue: Array<(token: string) => void> = []

export function setAccessToken(token: string | null) {
  accessToken = token
}

export function getAccessToken() {
  return accessToken
}

// ─── Axios instance ───────────────────────────────────────────────────────────

export const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // send httpOnly refresh-token cookie
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
})

// ─── Request interceptor — inject Bearer token ────────────────────────────────

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  return config
})

// ─── Response interceptor — refresh on 401 ───────────────────────────────────

const AUTH_NO_REFRESH_PATHS = [
  '/auth/me',
  '/auth/refresh',
  '/auth/login',
  '/auth/register',
  '/auth/logout',
  '/pos/auth/login',
  '/pos/auth/takeover',
]

function shouldAttemptTokenRefresh(url: string): boolean {
  if (AUTH_NO_REFRESH_PATHS.some((path) => url.includes(path))) {
    return false
  }
  // Only refresh when we had a session (access token) or another protected call failed.
  return !!accessToken
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    const requestUrl = original?.url ?? ''

    if (
      error.response?.status === 401 &&
      !original._retry &&
      shouldAttemptTokenRefresh(requestUrl)
    ) {
      original._retry = true

      if (isRefreshing) {
        return new Promise<string>((resolve) => {
          refreshQueue.push(resolve)
        }).then((token) => {
          original.headers.Authorization = `Bearer ${token}`
          return api(original)
        })
      }

      isRefreshing = true

      try {
        const { data } = await axios.post<AuthTokens>(
          `${BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true },
        )
        setAccessToken(data.access_token)
        refreshQueue.forEach((cb) => cb(data.access_token))
        refreshQueue = []
        original.headers.Authorization = `Bearer ${data.access_token}`
        return api(original)
      } catch {
        setAccessToken(null)
        refreshQueue = []
        return Promise.reject(error)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  },
)

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Extract a human-readable error message from an Axios error. */
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiError | undefined
    if (data?.message) return data.message
    if (error.message) return error.message
  }
  if (error instanceof Error) return error.message
  return 'An unexpected error occurred.'
}

/** Extract field-level validation errors from an Axios 422 response. */
export function getFieldErrors(error: unknown): Record<string, string> {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiError | undefined
    if (data?.errors) {
      return Object.fromEntries(
        Object.entries(data.errors).map(([k, v]) => [k, v[0]]),
      )
    }
  }
  return {}
}
